import { useState, useEffect } from 'react'
import {
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Target,
  Calendar,
  Lightbulb,
  Star,
  User,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import EmptyState from '../../components/ui/EmptyState'

interface FeedbackItem {
  id: number
  session_id?: number
  booking_id?: number
  interviewer_id: number
  status: string
  communication_score?: number
  technical_score?: number
  problem_solving_score?: number
  leadership_score?: number
  cultural_fit_score?: number
  overall_score?: number
  strengths: string[]
  improvements: string[]
  practice_actions: string[]
  summary?: string
  approved_at?: string
  delivered_at?: string
  created_at: string
  updated_at: string
  session_title?: string
  scheduled_at?: string
  interviewer_name?: string
}

const buildPdfHtml = (item: FeedbackItem): string => {
  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'

  const listHtml = (items: string[], color: string) =>
    items.length
      ? `<ul style="list-style:none;padding:0;margin:8px 0 0;">${items.map((s) => `<li style="padding:8px 12px 8px 16px;margin:6px 0;border-left:3px solid ${color};border-radius:4px;background:#f8f9fb;font-size:13px;line-height:1.6;">${s}</li>`).join('')}</ul>`
      : ''

  const section = (label: string, color: string, content: string) =>
    content
      ? `<div style="margin-bottom:20px;"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:${color};margin-bottom:6px;">${label}</div>${content}</div>`
      : ''

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${item.session_title || 'Feedback Report'}</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a202c;margin:0;padding:32px 40px;font-size:14px;}
    @page{margin:20mm 18mm;}
    @media print{body{padding:0;}}
    h1{font-size:20px;font-weight:700;margin:0 0 4px;}
    .meta{font-size:12px;color:#6b7280;margin-bottom:20px;}
    .badge{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(34,197,94,.1);color:#16a34a;font-size:11px;font-weight:700;margin-bottom:24px;}
    hr{border:none;border-top:1px solid #e5e7eb;margin:20px 0;}
  </style></head><body>
  <h1>${item.session_title || 'Interview Session'}</h1>
  <div class="meta">
    ${formatDate(item.scheduled_at || item.delivered_at || item.created_at)}
    ${item.interviewer_name ? ` &nbsp;·&nbsp; ${item.interviewer_name}` : ''}
    ${item.overall_score != null ? ` &nbsp;·&nbsp; Overall Score: ${item.overall_score.toFixed(1)} / 5.0` : ''}
  </div>
  <div class="badge">✓ Feedback Delivered</div>
  <hr>
  ${section('Session Summary', '#1e2432', item.summary ? `<p style="font-size:14px;line-height:1.75;margin:0;">${item.summary}</p>` : '')}
  ${section('Strengths', '#16a34a', listHtml(item.strengths, '#22c55e'))}
  ${section('Opportunities for Growth', '#d97706', listHtml(item.improvements, '#f59e0b'))}
  ${section('Practice Actions & Next Steps', '#1e2432', listHtml(item.practice_actions, '#1e2432'))}
  </body></html>`
}

const handleDownloadPdf = (item: FeedbackItem) => {
  const html = buildPdfHtml(item)
  const win = window.open('', '_blank')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.focus()
  setTimeout(() => {
    win.print()
  }, 400)
}

export default function ClientFeedback() {
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadFeedback = () => {
    setLoading(true)
    setError('')
    evaluationsApi
      .getFeedback()
      .then((data) => {
        const items = Array.isArray(data) ? data : (data as any)?.data ?? []
        setFeedbackList(items)
      })
      .catch((err) => {
        console.error('Failed to fetch feedback:', err)
        setError(err?.message || 'Failed to load feedback. Please try again.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadFeedback() }, [])

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) return <LoadingSpinner message="Loading feedback..." />

  return (
    <div className="client-feedback">
      <div className="page-header">
        <h1 className="page-title">View Feedback</h1>
        <p className="page-subtitle">
          Review detailed, structured feedback from your practice interviews
        </p>
      </div>

      {error && (
        <div className="cfb-error-banner">
          <span>{error}</span>
          <button type="button" onClick={loadFeedback} className="cfb-retry-btn">
            Try Again
          </button>
        </div>
      )}

      {!error && feedbackList.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No Feedback Reports Yet"
          message="Complete an interview session to receive detailed feedback from your interviewer."
        />
      ) : (
        <div className="cfb-list">
          {feedbackList.map((item) => {
            const isExpanded = expandedId === item.id
            const title = item.session_title || 'Interview Session'

            return (
              <div key={item.id} className="cfb-card">
                {/* ── Header ── */}
                <div
                  className="cfb-header"
                  onClick={() => toggleExpand(item.id)}
                >
                  <div className="cfb-header-left">
                    <h3 className="cfb-title">{title}</h3>
                    <div className="cfb-meta">
                      <span className="cfb-meta-item">
                        <Calendar size={13} />
                        {formatDate(item.scheduled_at || item.delivered_at || item.created_at)}
                      </span>
                      {item.interviewer_name && (
                        <span className="cfb-meta-item">
                          <User size={13} />
                          {item.interviewer_name}
                        </span>
                      )}
                      {item.overall_score && (
                        <span className="cfb-meta-item">
                          <Star size={13} />
                          Overall: {item.overall_score.toFixed(1)} / 5.0
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="cfb-header-right">
                    <span className="cfb-badge-delivered">
                      <CheckCircle size={13} />
                      Feedback Delivered
                    </span>

                    <div className="cfb-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="cfb-download-btn"
                        title="Download PDF"
                        type="button"
                        onClick={() => handleDownloadPdf(item)}
                      >
                        <Download size={16} />
                      </button>
                    </div>

                    <button className="cfb-expand-btn" type="button">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {/* ── Expanded content ── */}
                {isExpanded && (
                  <div className="cfb-body">
                    {/* Session Summary */}
                    {item.summary && (
                      <div className="cfb-section cfb-section-summary">
                        <div className="cfb-section-label">
                          <FileText size={14} />
                          Session Summary
                        </div>
                        <p className="cfb-summary-text">{item.summary}</p>
                      </div>
                    )}

                    {/* Strengths */}
                    {item.strengths.length > 0 && (
                      <div className="cfb-section">
                        <div className="cfb-section-label cfb-label-green">
                          <CheckCircle size={14} />
                          Strengths
                        </div>
                        <ul className="cfb-list-items">
                          {item.strengths.map((s, i) => (
                            <li key={i} className="cfb-list-item cfb-item-green">
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Opportunities for Growth */}
                    {item.improvements.length > 0 && (
                      <div className="cfb-section">
                        <div className="cfb-section-label cfb-label-amber">
                          <Target size={14} />
                          Opportunities for Growth
                        </div>
                        <ul className="cfb-list-items">
                          {item.improvements.map((s, i) => (
                            <li key={i} className="cfb-list-item cfb-item-amber">
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Practice Actions & Next Steps */}
                    {item.practice_actions.length > 0 && (
                      <div className="cfb-section">
                        <div className="cfb-section-label cfb-label-navy">
                          <Lightbulb size={14} />
                          Practice Actions &amp; Next Steps
                        </div>
                        <ul className="cfb-list-items">
                          {item.practice_actions.map((s, i) => (
                            <li key={i} className="cfb-list-item cfb-item-navy">
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <style>{`
        .cfb-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .cfb-card {
          background: white;
          border-radius: 14px;
          border: 1px solid var(--border);
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          transition: box-shadow 0.2s;
        }

        .cfb-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.09);
        }

        /* Header */
        .cfb-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          cursor: pointer;
          transition: background 0.15s;
          gap: 16px;
        }

        .cfb-header:hover {
          background: #f9fafb;
        }

        .cfb-header-left {
          flex: 1;
          min-width: 0;
        }

        .cfb-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 6px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cfb-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 14px;
        }

        .cfb-meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12.5px;
          color: var(--text-muted);
        }

        .cfb-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        .cfb-badge-delivered {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: rgba(34,197,94,0.1);
          color: #16a34a;
          white-space: nowrap;
        }

        .cfb-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .cfb-download-btn {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: white;
          color: var(--primary-navy);
          cursor: pointer;
          transition: all 0.15s;
          flex-shrink: 0;
        }

        .cfb-download-btn:hover {
          border-color: var(--primary-navy);
          background: rgba(45,55,72,0.05);
        }

        .cfb-expand-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.15s;
        }

        .cfb-expand-btn:hover {
          border-color: var(--primary-navy);
          color: var(--primary-navy);
        }

        /* Body */
        .cfb-body {
          border-top: 1px solid var(--border);
          background: #f8f9fb;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Sections */
        .cfb-section {
          background: white;
          border-radius: 10px;
          border: 1px solid var(--border);
          padding: 18px 20px;
        }

        .cfb-section-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 5px 10px;
          border-radius: 6px;
          background: rgba(45,55,72,0.07);
          color: var(--primary-navy);
          margin-bottom: 14px;
        }

        .cfb-label-green {
          background: rgba(34,197,94,0.1);
          color: #16a34a;
        }

        .cfb-label-amber {
          background: rgba(245,158,11,0.1);
          color: #d97706;
        }

        .cfb-label-navy {
          background: rgba(45,55,72,0.08);
          color: var(--primary-navy);
        }

        /* Summary */
        .cfb-section-summary {
          border-left: 3px solid var(--primary-navy);
        }

        .cfb-summary-text {
          font-size: 14px;
          line-height: 1.75;
          color: var(--text-dark);
          margin: 0;
        }

        /* List items */
        .cfb-list-items {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .cfb-list-item {
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid var(--border);
          font-size: 13.5px;
          color: var(--text-dark);
          line-height: 1.6;
        }

        .cfb-item-green {
          border-left: 3px solid #22c55e;
          background: rgba(34,197,94,0.03);
        }

        .cfb-item-amber {
          border-left: 3px solid #f59e0b;
          background: rgba(245,158,11,0.03);
        }

        .cfb-item-navy {
          border-left: 3px solid var(--primary-navy);
          background: rgba(45,55,72,0.03);
        }

        .cfb-error-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 20px;
          background: #fef2f2;
          border: 1px solid #fca5a5;
          border-radius: 10px;
          color: #dc2626;
          font-size: 14px;
          margin-bottom: 20px;
        }
        .cfb-retry-btn {
          padding: 6px 16px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }
        .cfb-retry-btn:hover { background: #b91c1c; }

        @media (max-width: 768px) {
          .cfb-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .cfb-header-right {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  )
}
